import { FC } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, ContentCopy, Delete } from '@mui/icons-material';
import { QuestionnaireFormDefinition, QuestionnaireResponseType } from '@app/api/QuestionnaireAdminStore';
import {
  copyInstrument,
  copyItem,
  duplicateAt,
  emptyChoice,
  emptyInstrument,
  emptyItem,
  moveInList,
  publishedCodes,
  removeAt,
  updateChoice,
  updateInstrument,
  updateInstruments,
  updateItem,
} from './questionnaireDefinitionEditing';
import { ScoredCodesLink } from './ScoredCodesLink';

interface Props {
  definition: QuestionnaireFormDefinition;
  // Left out for a read-only view.
  onChange?: (definition: QuestionnaireFormDefinition) => void;
  // The published version being edited, if any. Its codes are locked, since changing one would start a new
  // questionnaire, instrument or item instead of a new version of it.
  published?: QuestionnaireFormDefinition | null;
}

const responseTypeLabels: Record<QuestionnaireResponseType, string> = {
  [QuestionnaireResponseType.MutuallyExclusive]: 'Pick one choice',
  [QuestionnaireResponseType.Dropdown]: 'Pick one from a list',
  [QuestionnaireResponseType.FreeText]: 'Free text',
};

interface OrderControlsProps {
  label: string;
  index: number;
  count: number;
  onMove: (offset: number) => void;
  onRemove: () => void;
  // Left out where copying isn't offered.
  onDuplicate?: () => void;
}

const OrderControls: FC<OrderControlsProps> = ({ label, index, count, onMove, onRemove, onDuplicate }) => (
  <Box sx={{ display: 'flex', flexShrink: 0 }}>
    <IconButton size="small" aria-label={`Move ${label} up`} disabled={index === 0} onClick={() => onMove(-1)}>
      <ArrowUpward fontSize="small" />
    </IconButton>
    <IconButton size="small" aria-label={`Move ${label} down`} disabled={index === count - 1} onClick={() => onMove(1)}>
      <ArrowDownward fontSize="small" />
    </IconButton>
    {onDuplicate && (
      <IconButton size="small" aria-label={`Duplicate ${label}`} onClick={onDuplicate}>
        <ContentCopy fontSize="small" />
      </IconButton>
    )}
    <IconButton size="small" aria-label={`Remove ${label}`} disabled={count === 1} onClick={onRemove}>
      <Delete fontSize="small" />
    </IconButton>
  </Box>
);

/** Edits, or shows read-only, a questionnaire's instruments, items and choices. */
export const QuestionnaireDefinitionForm: FC<Props> = ({ definition, onChange, published = null }) => {
  const readOnly = !onChange;
  const change = onChange ?? (() => {});
  const locked = publishedCodes(published);

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Questionnaire name"
          value={definition.name}
          onChange={(e) => change({ ...definition, name: e.target.value })}
          disabled={readOnly}
          required
          sx={{ flex: 2, minWidth: 240 }}
          helperText="Participants see this above the questions."
        />
        <TextField
          label="Code"
          value={definition.code}
          onChange={(e) => change({ ...definition, code: e.target.value })}
          disabled={readOnly || !!published}
          required
          sx={{ flex: 1, minWidth: 160 }}
          helperText={
            <>
              {published ? 'Locked once published' : 'A stable ID, e.g. PHQ4'}
              {!readOnly && (
                <>
                  {' · '}
                  <ScoredCodesLink />
                </>
              )}
            </>
          }
        />
      </Box>

      {definition.instruments.map((instrument, instrumentIndex) => (
        <Paper key={instrumentIndex} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Instrument {instrumentIndex + 1}</Typography>
            {!readOnly && (
              <OrderControls
                label={`instrument ${instrumentIndex + 1}`}
                index={instrumentIndex}
                count={definition.instruments.length}
                onMove={(offset) =>
                  change(updateInstruments(definition, (list) => moveInList(list, instrumentIndex, offset)))
                }
                onRemove={() => change(updateInstruments(definition, (list) => removeAt(list, instrumentIndex)))}
                onDuplicate={() =>
                  change(updateInstruments(definition, (list) => duplicateAt(list, instrumentIndex, copyInstrument)))
                }
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Instrument name"
              value={instrument.name}
              onChange={(e) =>
                change(
                  updateInstrument(definition, instrumentIndex, (current) => ({ ...current, name: e.target.value }))
                )
              }
              disabled={readOnly}
              required
              size="small"
              sx={{ flex: 2, minWidth: 200 }}
            />
            <TextField
              label="Instrument code"
              value={instrument.code}
              onChange={(e) =>
                change(
                  updateInstrument(definition, instrumentIndex, (current) => ({ ...current, code: e.target.value }))
                )
              }
              disabled={readOnly || locked.instrumentCodes.has(instrument.code)}
              required
              size="small"
              sx={{ flex: 1, minWidth: 140 }}
              helperText={
                <>
                  Scoring is looked up by this code
                  {!readOnly && (
                    <>
                      {' · '}
                      <ScoredCodesLink />
                    </>
                  )}
                </>
              }
            />
          </Box>

          <Stack spacing={2}>
            {instrument.items.map((item, itemIndex) => {
              const changeItem = (patch: Partial<typeof item>) =>
                change(updateItem(definition, instrumentIndex, itemIndex, (current) => ({ ...current, ...patch })));
              return (
                <Box key={itemIndex} sx={{ borderLeft: 3, borderColor: 'divider', pl: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1">Item {itemIndex + 1}</Typography>
                    {!readOnly && (
                      <OrderControls
                        label={`item ${itemIndex + 1}`}
                        index={itemIndex}
                        count={instrument.items.length}
                        onMove={(offset) =>
                          change(
                            updateInstrument(definition, instrumentIndex, (current) => ({
                              ...current,
                              items: moveInList(current.items, itemIndex, offset),
                            }))
                          )
                        }
                        onRemove={() =>
                          change(
                            updateInstrument(definition, instrumentIndex, (current) => ({
                              ...current,
                              items: removeAt(current.items, itemIndex),
                            }))
                          )
                        }
                        onDuplicate={() =>
                          change(
                            updateInstrument(definition, instrumentIndex, (current) => ({
                              ...current,
                              items: duplicateAt(current.items, itemIndex, copyItem),
                            }))
                          )
                        }
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                      label="Item code"
                      value={item.code}
                      onChange={(e) => changeItem({ code: e.target.value })}
                      disabled={readOnly || locked.itemCodes.has(item.code)}
                      required
                      size="small"
                      sx={{ flex: 1, minWidth: 140 }}
                      helperText="Research joins on this, e.g. PHQ4_1"
                    />
                    <TextField
                      label="Subscale"
                      value={item.subscaleCode ?? ''}
                      onChange={(e) => changeItem({ subscaleCode: e.target.value })}
                      disabled={readOnly}
                      size="small"
                      sx={{ flex: 1, minWidth: 120 }}
                      helperText="Optional, e.g. GAD2"
                    />
                    <FormControl size="small" sx={{ flex: 1, minWidth: 160 }} disabled={readOnly}>
                      <InputLabel id={`response-type-${instrumentIndex}-${itemIndex}`}>Answer</InputLabel>
                      <Select
                        labelId={`response-type-${instrumentIndex}-${itemIndex}`}
                        label="Answer"
                        value={item.responseType}
                        onChange={(e) => changeItem({ responseType: e.target.value })}
                      >
                        {Object.values(QuestionnaireResponseType).map((responseType) => (
                          <MenuItem key={responseType} value={responseType}>
                            {responseTypeLabels[responseType]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Stack spacing={2}>
                    <TextField
                      label="Lead-in"
                      value={item.leadIn ?? ''}
                      onChange={(e) => changeItem({ leadIn: e.target.value })}
                      disabled={readOnly}
                      size="small"
                      multiline
                      fullWidth
                      helperText="Optional, shown above the question, e.g. Over the last 2 weeks…"
                    />
                    <TextField
                      label="Question"
                      value={item.text}
                      onChange={(e) => changeItem({ text: e.target.value })}
                      disabled={readOnly}
                      required
                      size="small"
                      multiline
                      fullWidth
                    />
                  </Stack>

                  {item.responseType !== QuestionnaireResponseType.FreeText && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Choices
                      </Typography>
                      {item.choices.map((choice, choiceIndex) => (
                        <Box key={choiceIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            label={`Choice ${choiceIndex + 1}`}
                            value={choice.text}
                            onChange={(e) =>
                              change(
                                updateChoice(definition, instrumentIndex, itemIndex, choiceIndex, (current) => ({
                                  ...current,
                                  text: e.target.value,
                                }))
                              )
                            }
                            disabled={readOnly}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Score"
                            type="number"
                            value={choice.scoreValue ?? ''}
                            onChange={(e) =>
                              change(
                                updateChoice(definition, instrumentIndex, itemIndex, choiceIndex, (current) => ({
                                  ...current,
                                  scoreValue: e.target.value === '' ? null : Number(e.target.value),
                                }))
                              )
                            }
                            disabled={readOnly}
                            size="small"
                            sx={{ width: 100 }}
                          />
                          {!readOnly && (
                            <OrderControls
                              label={`choice ${choiceIndex + 1}`}
                              index={choiceIndex}
                              count={item.choices.length}
                              onMove={(offset) =>
                                changeItem({ choices: moveInList(item.choices, choiceIndex, offset) })
                              }
                              onRemove={() => changeItem({ choices: removeAt(item.choices, choiceIndex) })}
                            />
                          )}
                        </Box>
                      ))}
                      {!readOnly && (
                        <Box>
                          <Button
                            size="small"
                            startIcon={<Add />}
                            onClick={() => changeItem({ choices: [...item.choices, emptyChoice()] })}
                          >
                            Add choice
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  )}
                </Box>
              );
            })}
            {!readOnly && (
              <Box>
                <Button
                  startIcon={<Add />}
                  onClick={() =>
                    change(
                      updateInstrument(definition, instrumentIndex, (current) => ({
                        ...current,
                        items: [...current.items, emptyItem()],
                      }))
                    )
                  }
                >
                  Add item
                </Button>
              </Box>
            )}
          </Stack>
        </Paper>
      ))}

      {!readOnly && (
        <Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => change(updateInstruments(definition, (list) => [...list, emptyInstrument()]))}
          >
            Add instrument
          </Button>
        </Box>
      )}
    </Stack>
  );
};
